/**
 * Presentation helpers for the MONTH-GROUPED manager timeline in the pipeline
 * activity log drawer (SE-6554 restyle of SE-5504 AC3).
 *
 * Separate from `pipelineActivityRows.ts` on purpose. That module owns the
 * audit-trail row model shared by BOTH activity surfaces — the card-scoped
 * trail in the player preview drawer and the whole-pipeline manager log. This
 * module owns only the manager log's presentation: month headers, a relative
 * timestamp, and the one-line event sentence. Nothing here is reachable from
 * the shared trail, so restyling the manager log cannot move the preview
 * drawer's pixels.
 *
 * Like its sibling it lives in `utils/` rather than beside the View because
 * component directories are restricted to index/Container/View files by the
 * component-structure lint rule.
 *
 * Everything here reads timestamps in UTC, matching
 * `formatActivityTimestamp` — an audit surface whose grouping shifted with the
 * viewer's timezone would put the same entry under different month headers on
 * two machines, and the existing tests pin UTC for exactly that reason.
 * @module features/player-kanban/utils/pipelineActivityTimeline
 */

import { PipelineActivityAction } from "@/generated/graphql";

import type { PipelineActivityRow } from "./pipelineActivityRows";

/** One month's worth of entries, newest month first. */
export interface PipelineActivityMonthGroup {
  /** Stable group key (`2026-06`), also the react key. */
  readonly key: string;
  /** Header text, e.g. `JULY 2026`. */
  readonly label: string;
  /** The month's entries, in the order they were received (newest-first). */
  readonly rows: readonly PipelineActivityRow[];
}

/** Month names for the group headers. Uppercased at render by the label. */
const MONTH_NAME = [
  "JANUARY",
  "FEBRUARY",
  "MARCH",
  "APRIL",
  "MAY",
  "JUNE",
  "JULY",
  "AUGUST",
  "SEPTEMBER",
  "OCTOBER",
  "NOVEMBER",
  "DECEMBER",
] as const;

/** Short month names for the absolute fallback timestamp (`Jun 24`). */
const SHORT_MONTH_NAME = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** Header shown for entries whose timestamp will not parse. */
const UNDATED_LABEL = "UNDATED";

/** Group key for the unparseable bucket — sorts nowhere, stays last. */
const UNDATED_KEY = "undated";

/** Beyond this many days the stamp becomes an absolute date. */
const RELATIVE_DAY_LIMIT = 7;

/** Milliseconds in a day, for the whole-day difference. */
const MS_PER_DAY = 86_400_000;

/**
 * Parse an ISO timestamp, returning `null` rather than an Invalid Date.
 * @param iso - The ISO timestamp.
 * @returns The parsed date, or `null` when absent or unparseable.
 */
const parseIso = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * UTC midnight for a date, as epoch milliseconds.
 *
 * Whole-DAY differences are what "2 days ago" means; subtracting raw
 * timestamps would call 23:00 yesterday → 01:00 today "0 days ago".
 * @param date - The date to floor.
 * @returns Epoch ms at that date's UTC midnight.
 */
const utcMidnight = (date: Date): number =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

/**
 * Format an entry's timestamp for the manager timeline.
 *
 * Recent entries read relatively ("2 days ago") because that is how a manager
 * scanning a feed judges freshness; older ones fall back to an absolute date
 * ("Jun 24") with no year, because the MONTH HEADER above the entry already
 * carries the year. The two formats therefore never lose information between
 * them.
 * @param iso - The entry's ISO timestamp.
 * @param now - Reference point, injectable so tests are deterministic.
 * @returns The display stamp, or an em dash when the timestamp is unusable.
 */
export const formatActivityRelativeTimestamp = (
  iso: string | null | undefined,
  now: Date = new Date()
): string => {
  const parsed = parseIso(iso);
  if (!parsed) return "—";

  const days = Math.round(
    (utcMidnight(now) - utcMidnight(parsed)) / MS_PER_DAY
  );

  // A future timestamp (clock skew between the server and this device) has no
  // honest relative phrasing — fall through to the absolute date.
  if (days < 0 || days >= RELATIVE_DAY_LIMIT) {
    return `${SHORT_MONTH_NAME[parsed.getUTCMonth()]} ${parsed.getUTCDate()}`;
  }
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
};

/**
 * The month bucket an entry belongs to.
 *
 * An entry whose timestamp will not parse still gets a bucket rather than being
 * discarded — see {@link groupActivityRowsByMonth}.
 * @param row - The entry.
 * @returns Its group key and header label.
 */
const monthOf = (
  row: PipelineActivityRow
): { readonly key: string; readonly label: string } => {
  const parsed = parseIso(row.occurredAt);
  if (!parsed) return { key: UNDATED_KEY, label: UNDATED_LABEL };
  const month = parsed.getUTCMonth();
  const year = parsed.getUTCFullYear();
  return {
    key: `${year}-${String(month + 1).padStart(2, "0")}`,
    label: `${MONTH_NAME[month]} ${year}`,
  };
};

/**
 * Group rows into months, preserving the order they arrive in.
 *
 * The caller has already ordered rows newest-first, so walking them in order
 * yields months newest-first and entries newest-first WITHIN each month without
 * a second sort. Grouping is not filtering: every row lands in exactly one
 * group, including rows whose timestamp will not parse — those collect under an
 * `UNDATED` header rather than vanishing, which is the same
 * "surface the backend, never mask it" rule the row model follows.
 * @param rows - Rows ordered newest-first.
 * @returns One group per month present, in the rows' own order.
 */
export const groupActivityRowsByMonth = (
  rows: readonly PipelineActivityRow[]
): readonly PipelineActivityMonthGroup[] => {
  const tagged = rows.map(row => ({ row, ...monthOf(row) }));

  // A Set preserves insertion order, so the month keys come out in the order
  // the rows introduced them — newest-first, given a newest-first input — and
  // `filter` preserves the row order inside each bucket. Two passes rather than
  // one accumulator because `functional/immutable-data` forbids growing a Map
  // or an array in place, and a spread-per-row reduce would be quadratic.
  return [...new Set(tagged.map(entry => entry.key))].map(key => {
    const members = tagged.filter(entry => entry.key === key);
    return {
      key,
      label: members[0].label,
      rows: members.map(entry => entry.row),
    };
  });
};

/**
 * The stage-to-stage arrow for a move entry, or `null` for anything else.
 * @param row - The entry.
 * @returns e.g. `Evaluation → Negotiation`, or `null`.
 */
const describeMoveArrow = (row: PipelineActivityRow): string | null =>
  row.action === PipelineActivityAction.Moved &&
  row.fromPhaseName !== null &&
  row.toPhaseName !== null
    ? `${row.fromPhaseName} → ${row.toPhaseName}`
    : null;

/**
 * What the actor did, without naming the actor.
 * @param row - The entry.
 * @returns The predicate half of the headline.
 */
const describePredicate = (row: PipelineActivityRow): string => {
  const arrow = describeMoveArrow(row);
  if (arrow !== null && row.playerName !== null) {
    return `moved ${row.playerName} · ${arrow}`;
  }
  if (arrow !== null) return `moved a card · ${arrow}`;
  if (row.playerName !== null)
    return `· ${row.description} — ${row.playerName}`;
  return `· ${row.description}`;
};

/**
 * The single line of text that heads an entry in the manager timeline.
 *
 * A rule-driven entry heads with the RULE's name and a system entry with
 * "System" (SE-6864) — never with a person's name. The bolt glyph and the
 * deliberate ABSENCE of an avatar are added by the View, not spliced into this
 * string, so both stay addressable as their own elements, and they are what
 * keeps a rule name from reading as somebody's action.
 * @param row - The entry.
 * @returns The headline, e.g. `Pietra moved Emile Rensson · Evaluation → Negotiation`.
 */
export const describeActivityHeadline = (row: PipelineActivityRow): string =>
  `${row.actorLabel} ${describePredicate(row)}`;
